import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Package, Smartphone, Apple, ChevronRight, Globe, Bot, Code2, Terminal } from 'lucide-react'

interface App {
  app_id: string
  name: string
  description: string
  bundle_id: string
  category: string
  icon_url: string
  app_type?: string
  public_download?: boolean
  latest_version?: string
  total_downloads?: number
  versions?: { platform: string; version_name?: string }[]
}

export default function Apps() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['apps'],
    queryFn: async () => {
      const res = await fetch('/api/apps')
      if (!res.ok) {
        // Found 2026-10-22 debugging an "empty/stuck" app list report: this previously
        // called res.json() unconditionally, so a non-2xx response with a JSON error body
        // (e.g. {"detail": "..."}) was silently treated as success data -- apps.length
        // just came out 0, indistinguishable from "no apps registered". Surfacing the
        // real status makes a future failure show an actual error instead.
        throw new Error(`GET /api/apps failed: ${res.status} ${res.statusText}`)
      }
      return res.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-12 bg-red-500/10 rounded-xl border border-red-500/30">
        <h3 className="text-lg font-medium text-red-400">Couldn't load apps</h3>
        <p className="text-gray-500 mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    )
  }

  const apps: App[] = data?.apps || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Applications</h2>
          <p className="text-gray-400">{apps.length} apps available</p>
        </div>
      </div>

      {apps.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {apps.map((app) => {
            const hasAndroid = app.versions?.some((v) => v.platform === 'android')
            const hasIOS = app.versions?.some((v) => v.platform === 'ios')
            const hasVSCode = app.versions?.some((v) => v.platform === 'vscode')
            const hasCLI = app.versions?.some((v) => v.platform === 'cli')

            return (
              <Link
                key={app.app_id}
                to={`/download/${app.app_id}`}
                className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-primary-500/50 transition-all hover:translate-y-[-2px]"
              >
                <div className="flex items-start gap-4">
                  {app.icon_url ? (
                    <img
                      src={app.icon_url}
                      alt={app.name}
                      className="w-16 h-16 rounded-xl"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-primary-500/20 rounded-xl flex items-center justify-center">
                      <Package className="w-8 h-8 text-primary-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg truncate">{app.name}</h3>
                      {app.latest_version && (
                        <span className="text-xs bg-primary-500/20 text-primary-400 px-1.5 py-0.5 rounded font-mono">
                          v{app.latest_version}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 capitalize">{app.category}</p>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {app.description || 'No description'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10 flex-wrap">
                  {app.app_type === 'agent' && (
                    <span className="flex items-center gap-1 text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded">
                      <Bot className="w-3 h-3" />
                      Agent
                    </span>
                  )}
                  {app.app_type === 'mcp_tool' && (
                    <span className="flex items-center gap-1 text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">
                      <Bot className="w-3 h-3" />
                      MCP Tool
                    </span>
                  )}
                  {hasAndroid && (
                    <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                      <Smartphone className="w-3 h-3" />
                      Android
                    </span>
                  )}
                  {hasIOS && (
                    <span className="flex items-center gap-1 text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">
                      <Apple className="w-3 h-3" />
                      iOS
                    </span>
                  )}
                  {hasVSCode && (
                    <span className="flex items-center gap-1 text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                      <Code2 className="w-3 h-3" />
                      VS Code
                    </span>
                  )}
                  {hasCLI && (
                    <span className="flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                      <Terminal className="w-3 h-3" />
                      CLI
                    </span>
                  )}
                  {!hasAndroid && !hasIOS && !hasVSCode && !hasCLI && !app.app_type?.match(/agent|mcp_tool/) && (
                    <span className="text-xs text-gray-500">No versions yet</span>
                  )}
                  {app.public_download && (
                    <span className="flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded ml-auto">
                      <Globe className="w-3 h-3" />
                      Public
                    </span>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
          <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400">No Apps Yet</h3>
          <p className="text-gray-500 mt-1">Apps will appear here once registered</p>
        </div>
      )}
    </div>
  )
}
